import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as panelAccessService from '../../services/panelAccessService';

export const fetchIncomingRequests = createAsyncThunk(
    'panelAccess/fetchIncomingRequests',
    async (_, { rejectWithValue }) => {
        try {
            return await panelAccessService.getIncomingRequests();
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to fetch incoming requests');
        }
    }
);

export const fetchOutgoingRequests = createAsyncThunk(
    'panelAccess/fetchOutgoingRequests',
    async (_, { rejectWithValue }) => {
        try {
            return await panelAccessService.getOutgoingRequests();
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to fetch outgoing requests');
        }
    }
);

export const fetchApprovedAccess = createAsyncThunk(
    'panelAccess/fetchApprovedAccess',
    async (_, { rejectWithValue }) => {
        try {
            return await panelAccessService.getApprovedAccess();
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to fetch approved access');
        }
    }
);

export const fetchGrantedAccess = createAsyncThunk(
    'panelAccess/fetchGrantedAccess',
    async (_, { rejectWithValue }) => {
        try {
            return await panelAccessService.getGrantedToUsers();
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to fetch granted access');
        }
    }
);

export const requestPanelAccess = createAsyncThunk(
    'panelAccess/requestPanelAccess',
    async (targetUserId, { rejectWithValue, dispatch }) => {
        try {
            const result = await panelAccessService.requestAccess(targetUserId);
            dispatch(fetchOutgoingRequests());
            return result;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to request access');
        }
    }
);

export const grantPanelAccess = createAsyncThunk(
    'panelAccess/grantPanelAccess',
    async (targetUserId, { rejectWithValue, dispatch }) => {
        try {
            const result = await panelAccessService.grantAccess(targetUserId);
            dispatch(fetchGrantedAccess());
            return result;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to grant access');
        }
    }
);

export const approvePanelRequest = createAsyncThunk(
    'panelAccess/approvePanelRequest',
    async (id, { rejectWithValue, dispatch }) => {
        try {
            const result = await panelAccessService.approveRequest(id);
            dispatch(fetchIncomingRequests());
            dispatch(fetchGrantedAccess());
            dispatch(fetchApprovedAccess());
            return result;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to approve request');
        }
    }
);

export const rejectPanelRequest = createAsyncThunk(
    'panelAccess/rejectPanelRequest',
    async (id, { rejectWithValue, dispatch }) => {
        try {
            const result = await panelAccessService.rejectRequest(id);
            dispatch(fetchIncomingRequests());
            return result;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to reject request');
        }
    }
);

export const revokePanelAccess = createAsyncThunk(
    'panelAccess/revokePanelAccess',
    async (id, { rejectWithValue, dispatch }) => {
        try {
            const result = await panelAccessService.revokeAccess(id);
            dispatch(fetchApprovedAccess());
            dispatch(fetchGrantedAccess());
            dispatch(fetchIncomingRequests());
            dispatch(fetchOutgoingRequests());
            return result;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to revoke access');
        }
    }
);

const upsertById = (list, item) => {
    const idx = list.findIndex((r) => r.id === item.id);
    if (idx >= 0) {
        list[idx] = item;
    } else {
        list.unshift(item);
    }
};

const removeById = (list, id) => {
    const idx = list.findIndex((r) => r.id === id);
    if (idx >= 0) {
        list.splice(idx, 1);
    }
};

const initialState = {
    incomingRequests: [],
    outgoingRequests: [],
    approvedAccess: [],
    grantedAccess: [],
    fabPanelNotifications: [],
    unseenPanelEventCount: 0,
    loading: false,
    error: null,
};

const panelAccessSlice = createSlice({
    name: 'panelAccess',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        receivePanelAccessEvent: (state, action) => {
            const { type: eventType, request, currentUserId } = action.payload;
            if (!request?.id || !currentUserId) return;

            const isTarget = request.targetUserId === currentUserId;
            const isRequester = request.requesterId === currentUserId;
            const status = request.status;

            if (isTarget) {
                if (status === 'PENDING') {
                    upsertById(state.incomingRequests, request);
                } else {
                    removeById(state.incomingRequests, request.id);
                }
                if (status === 'APPROVED') {
                    upsertById(state.grantedAccess, request);
                } else if (status === 'REVOKED' || status === 'REJECTED') {
                    removeById(state.grantedAccess, request.id);
                }
            }

            if (isRequester) {
                if (status === 'PENDING') {
                    upsertById(state.outgoingRequests, request);
                } else {
                    removeById(state.outgoingRequests, request.id);
                }
                if (status === 'APPROVED') {
                    upsertById(state.approvedAccess, request);
                } else if (status === 'REVOKED' || status === 'REJECTED') {
                    removeById(state.approvedAccess, request.id);
                }
            }

            const isAccessGrantedToMe =
                isRequester &&
                (eventType === 'PANEL_ACCESS_GRANTED' || eventType === 'PANEL_ACCESS_APPROVED');

            if (isAccessGrantedToMe) {
                const fabKey = `${eventType}-${request.id}`;
                const exists = state.fabPanelNotifications.some((n) => n.fabKey === fabKey);
                if (!exists) {
                    state.fabPanelNotifications.unshift({
                        fabKey,
                        eventType,
                        request,
                        receivedAt: new Date().toISOString(),
                    });
                    if (state.fabPanelNotifications.length > 20) {
                        state.fabPanelNotifications.length = 20;
                    }
                    state.unseenPanelEventCount += 1;
                }
            }
        },
        markPanelEventsSeen: (state) => {
            state.unseenPanelEventCount = 0;
        },
        dismissFabPanelNotification: (state, action) => {
            const fabKey = action.payload;
            state.fabPanelNotifications = state.fabPanelNotifications.filter(
                (n) => n.fabKey !== fabKey
            );
        },
        clearAllFabPanelNotifications: (state) => {
            state.fabPanelNotifications = [];
            state.unseenPanelEventCount = 0;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchIncomingRequests.pending, (state) => { state.loading = true; })
            .addCase(fetchIncomingRequests.fulfilled, (state, action) => {
                state.loading = false;
                state.incomingRequests = action.payload;
            })
            .addCase(fetchIncomingRequests.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchOutgoingRequests.fulfilled, (state, action) => {
                state.outgoingRequests = action.payload;
            })
            .addCase(fetchApprovedAccess.fulfilled, (state, action) => {
                state.approvedAccess = (action.payload || []).filter((r) => r.status === 'APPROVED');
            })
            .addCase(fetchGrantedAccess.fulfilled, (state, action) => {
                state.grantedAccess = action.payload;
            });
    },
});

export const { clearError, receivePanelAccessEvent, markPanelEventsSeen, dismissFabPanelNotification, clearAllFabPanelNotifications } = panelAccessSlice.actions;
export default panelAccessSlice.reducer;
